import {
  forwardRef,
  useCallback,
  useRef,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '../lib/utils.js';
import { focusRing } from '../lib/focus.js';

/* ===========================================================================
 * SearchField — the toolbar's first slot, and the field a reader compares
 * across screens.
 *
 * WHY IT IS NOT JUST AN `<Input>`. `controlBarSearchClass` already gives 34
 * control rows one WIDTH for their search box, and that fixed the complaint it
 * was written for. What it cannot fix is that a plain bordered input is the same
 * object as the eight other bordered inputs on an employee form — so the one
 * field a reader reaches for first looks like a field they have to fill in. The
 * handoff answers that with a different anatomy rather than a different size: a
 * recessed fill instead of a border, a leading magnifier, and a keyboard hint on
 * the trailing edge. It reads as a *place to type a query* before it is read as
 * a form control.
 *
 * `controlBarSearchClass` IS NOT DEPRECATED BY THIS. Thirty-four screens spread
 * it onto a plain `<Input>` and they keep working; the export is unchanged. This
 * is the richer option for a screen that wants the handoff's toolbar, not a
 * forced migration — see the note on it in ControlBar.tsx.
 *
 * ---------------------------------------------------------------------------
 * WIDTH IS A FLOOR PLUS A GROWTH RULE, NOT A NUMBER.
 *
 * `min-w-search flex-search` — 216px minimum, `flex: 1 1 240px`. The search
 * field is the toolbar's one flexible member: it takes the slack on a wide
 * screen and gives it back before anything else has to wrap. A definite width
 * here is what made the old bar wrap early on a laptop while leaving a gap on a
 * monitor, and `--search-width` is a FLOOR for exactly that reason (see the long
 * note on it in design-tokens.css). Below 216px you cannot see what you typed,
 * which is the only hard requirement a search box has.
 *
 * ---------------------------------------------------------------------------
 * FOCUS. The house treatment is `focusRing` — a 2px outline held 2px off the
 * control — and this component deliberately does not use it.
 *
 * The reason is that the focusable thing here is the bare `<input>` and the
 * PAINTED thing is its `<label>` wrapper, so a ring on the input would be drawn
 * inside the fill, around a borderless box, in the wrong place. The label
 * therefore carries the treatment on `focus-within`, and it is the field
 * dialect the token layer already documents: the transparent border becomes
 * `--primary` and a 3px brand halo sits against it. That is the same pairing
 * `control.ts` describes for `<Input>`/`<Select>`/`<Combobox>` — the solid
 * border carries the 3:1 the indicator needs, the halo is the polish — so the
 * search box focuses like every other field rather than like a button.
 *
 * The recessed fill also LIFTS to `--surface` on focus. That is not decoration:
 * the field's resting state says "not currently in use", and going white is what
 * says the caret is now in here.
 *
 * ---------------------------------------------------------------------------
 * GEOMETRY is `min-height` plus padding, never a fixed height, so an Arabic or
 * German placeholder that wraps grows the field instead of clipping it —
 * `Button.tsx` established that anatomy and the handoff's README says the
 * primitive's anatomy wins over the mock's `height: 38px`.
 *
 * RTL: the magnifier and the kbd are flex children on the inline axis, so they
 * swap ends with `dir` on their own. No physical padding or positioning anywhere.
 * ======================================================================== */

export interface SearchFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  /**
   * The field's accessible name — "Search workers, sites, Emirates ID…".
   * REQUIRED, and not decoration: this field has no visible label, so without
   * it the control is announced as an unlabelled text box. It also becomes the
   * placeholder unless one is passed, so the visible prompt and the announced
   * name cannot drift apart.
   */
  label: string;
  /**
   * The keyboard shortcut that focuses this field, drawn as a `<kbd>` on the
   * trailing edge — "⌘K". Rendered only when passed AND only on a device with a
   * fine pointer: a keyboard hint on a camp tablet is a promise the device
   * cannot keep.
   */
  shortcut?: ReactNode;
  /**
   * Empty the field — OPTIONAL, and now only for a screen that has to do
   * something else at the same time (drop a selection, reset a page cursor).
   *
   * WHY THIS BELONGS TO THE PRIMITIVE. The input sets
   * `[&::-webkit-search-cancel-button]:appearance-none` — the native × is a
   * second, unstyled control inside a field that already has a shape — so
   * SUPPRESSING it is this component's decision, and replacing it is therefore
   * this component's job. Left to callers, "clear the search" becomes a
   * different affordance on each of the thirty-odd screens that use this, which
   * is the second control language §6 exists to prevent.
   *
   * IT USED TO BE THE ONLY WAY TO GET THE ×, AND THAT WAS THE BUG. The reason
   * written here for keeping it opt-in was that a field whose owner offers no
   * way to empty it would otherwise grow a button that cannot honestly do
   * anything — sound, and it turned out to describe nothing: an audit of all 32
   * `<SearchField>`s in the app found every one of them a controlled
   * `value`/`onChange` pair, and exactly TWO passing `onClear`. So the reasoning
   * protected a case that does not exist while shipping thirty search boxes a
   * reader has to backspace out by hand, next to two that clear on a tap.
   *
   * The × is therefore drawn WHENEVER THE FIELD CAN HONESTLY BE EMPTIED —
   * controlled, not read-only, not disabled, non-empty, and with either this
   * prop or an `onChange` to route the clearing through. The honesty test that
   * motivated the opt-in is kept; it is just asked of the props rather than
   * assumed from their absence. A field with `value` and no `onChange` still
   * gets nothing.
   */
  onClear?: () => void;
  /**
   * The clear button's accessible name — "Clear the search". REQUIRED, and it
   * used to fall back to a hard-coded English 'Clear'. Twenty-eight of the
   * thirty search boxes in the app took that fallback, so an Arabic screen
   * reader was told "Clear" in English on all but two of them. A default in a
   * primitive that cannot reach i18n is a default nobody can see is wrong;
   * requiring it moves the failure to the compiler. `SearchBox` in the app
   * supplies it once so no call site repeats it.
   */
  clearLabel: string;
  /** Classes for the field's outer box (the painted part). */
  className?: string;
  /** Classes for the `<input>` itself. Rarely needed. */
  inputClassName?: string;
}

export const SearchField = forwardRef<HTMLInputElement, SearchFieldProps>(
  // `disabled` is destructured and then passed back down, rather than left to
  // ride in `...props`, because the PAINTED element here is the `<label>` and a
  // `disabled:` variant can only ever match the `<input>`. Tailwind's `has-[]`
  // would express "a label containing a disabled input", but reading the prop is
  // version-proof and says the same thing.
  (
    {
      label,
      shortcut,
      onClear,
      clearLabel,
      className,
      inputClassName,
      placeholder,
      disabled,
      ...props
    },
    ref
  ) => {
    /* THE FIELD'S OWN HANDLE ON ITSELF, so the default × can empty a field
       whose owner passed only `value` and `onChange`.

       React does not expose "fire this input's onChange with a new value", and
       calling the handler with a hand-built object would hand the screen a fake
       event — `e.target.value` is the one thing every call site reads, and half
       of them read `e.target` for other reasons too. So the clear writes through
       the NATIVE value setter and dispatches a real `input` event: React's own
       listener is on `input`, so the screen's `onChange` runs with a genuine
       event whose target is genuinely this element.

       Merged with the forwarded ref rather than replacing it — the shell focuses
       the toolbar's search on ⌘K through that ref, and a component that
       swallowed it would break the shortcut it draws a hint for. */
    const inner = useRef<HTMLInputElement | null>(null);
    const attach = useCallback(
      (node: HTMLInputElement | null) => {
        inner.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      },
      [ref]
    );

    const { onChange, readOnly, value } = props;

    // Can this field honestly be emptied? See the note on `onClear`.
    const clearable =
      !disabled &&
      readOnly !== true &&
      value !== undefined &&
      value !== '' &&
      (onClear !== undefined || onChange !== undefined);

    function clear(): void {
      if (onClear) {
        onClear();
      } else {
        const el = inner.current;
        const setter = Object.getOwnPropertyDescriptor(
          window.HTMLInputElement.prototype,
          'value'
        )?.set;
        if (el && setter) {
          setter.call(el, '');
          el.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }
      // The caret goes back where the reader was typing, rather than leaving
      // them to click into an empty box they just emptied.
      inner.current?.focus();
    }

    return (
    // A `<label>`, so clicking anywhere in the box — the magnifier, the padding
    // — puts the caret in the field. That is most of the reason the whole thing
    // is one recessed rectangle rather than an icon beside an input.
    <label
      className={cn(
        'flex items-center gap-element',
        'min-w-search flex-search min-h-control rounded-control px-3 py-1.5',
        // The recessed fill, and a border that is PRESENT but transparent —
        // reserving the 1px so the box does not change size when focus paints
        // it. A border that appears from nothing shifts every sibling in the bar.
        'border border-transparent bg-surface-2',
        'transition-[background-color,border-color,box-shadow] duration-fast ease-standard',
        'focus-within:border-primary focus-within:bg-surface',
        // The 3px brand halo. `ring` and not `outline` here precisely because
        // there is no offset to paint: it hugs the border it belongs to. The
        // outset `focusRing` is for controls whose own edge is the indicator.
        'focus-within:ring-[3px] focus-within:ring-primary/15',
        // 50% is the system's ONE disabled fade (see `disabledFade` in
        // lib/focus.ts — it is a `disabled:` variant, which cannot reach a
        // parent, so the value is restated rather than the rule reinvented).
        // `transition-none` is the other half: a disabled control never animates,
        // because motion promises something will happen (rules §1).
        disabled && 'cursor-not-allowed opacity-50 transition-none',
        className
      )}
    >
      <Search
        size={16}
        // Decorative: the field's name comes from `label` below, and a
        // magnifier announced beside it would be read twice.
        aria-hidden="true"
        className="flex-none text-foreground-tertiary"
      />
      <input
        ref={attach}
        type="search"
        disabled={disabled}
        aria-label={label}
        placeholder={placeholder ?? label}
        className={cn(
          // Bare on purpose — the box around it is the control. `min-w-0` is
          // what lets the field shrink inside a wrapping toolbar instead of
          // forcing the bar wider than its line.
          'w-full min-w-0 flex-1 border-0 bg-transparent p-0 text-copy text-foreground',
          'placeholder:text-foreground-tertiary',
          // The input never draws its own focus treatment — the label does.
          'focus:outline-none focus-visible:outline-none',
          // Mobile Safari force-zooms the viewport on any focused field under
          // 16px. Same coarse-pointer escape the rest of the control family
          // uses, so the console keeps its density where that browser isn't.
          '[@media(pointer:coarse)]:text-base',
          // WebKit's own clear button is a second, unstyled control inside a
          // field that already has a shape of its own.
          '[&::-webkit-search-cancel-button]:appearance-none',
          inputClassName
        )}
        {...props}
      />
      {/* THE CLEAR ×, and only while there is something to clear. A button
          that is always present but inert half the time is one a reader stops
          reading; appearing with the first keystroke also makes it the nearest
          control to the text it removes.

          Inside the `<label>` on purpose: the label's own behaviour puts the
          caret back in the field, so clearing leaves the reader typing again
          rather than looking for the box. `type="button"` because this
          component is used inside forms (the worksite picker renders in one)
          and a bare button there submits it.

          `size-6` with a `-me-1` pull: the 24px target sits inside the field's
          12px inline padding rather than widening the box — which is what let
          this become the default across all 32 fields without any of them
          changing width or wrapping their toolbar. */}
      {clearable && (
        <button
          type="button"
          onClick={clear}
          aria-label={clearLabel}
          className={cn(
            '-me-1 flex size-6 flex-none items-center justify-center rounded-full',
            'text-foreground-tertiary hover:bg-surface hover:text-foreground',
            'transition-colors duration-fast ease-standard',
            focusRing
          )}
        >
          <X size={14} aria-hidden="true" />
        </button>
      )}
      {shortcut != null && shortcut !== false && shortcut !== '' && (
        <kbd
          // `aria-hidden`: it is a hint about a shortcut, not part of the
          // field's name, and "command K" read after the placeholder is noise.
          aria-hidden="true"
          className={cn(
            'flex-none rounded-2xs border border-border bg-surface px-1.5 py-0.5',
            // 11px, and this is the step the token layer grants for a datum
            // that is GLANCED at rather than read along a line — a key cap is
            // two glyphs, not a word. §1's 11px ban is about labels and header
            // rows, which this is not; `--text-badge` is still off-limits.
            'text-dense font-semibold leading-tight text-foreground-tertiary',
            // A keyboard hint on a finger-driven device is a lie.
            '[@media(pointer:coarse)]:hidden'
          )}
        >
          {shortcut}
        </kbd>
      )}
    </label>
    );
  }
);
SearchField.displayName = 'SearchField';
